import {
  AddIcon,
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  DeleteIcon,
  EditIcon,
  EmptyState,
  Grid,
  HTMLDisplay,
  InfiniteScrollTrigger,
  ResourceMenuActions,
  Skeleton,
  Stack,
} from '@all-manager/design-system-v2';
import CreateUpdatePrivateNoteModal from "../../../components/modals/CreateUpdatePrivateNoteModal";
import DeleteNoteModal from "../../../components/modals/DeleteNoteModal";
import FullPageLayout from "../../../layout/FullPageLayout/FullPageLayout";
import NoNotesIcon from "../assets/no-notes.svg?react";
import { usePrivateNotes } from './usePrivateNotes';

const SKELETON_CARDS = ['first', 'second', 'third'];
const NOTE_CARD_SIZE = { xs: 12, md: 6, lg: 4 } as const;
const SKELETON_HEIGHT = 160;

export default function PrivateNotes() {
  const hook = usePrivateNotes();

  const newNoteButton = hook.isAdmin ? (
    <Button
      variant="contained"
      size="small"
      startIcon={<AddIcon />}
      onClick={hook.handleOpenCreationModal}
    >
      Nouvelle note
    </Button>
  ) : undefined;

  function renderContent() {
    if (hook.isLoading) {
      return (
        <Grid container spacing={2} aria-busy="true" aria-label="Chargement des notes">
          {SKELETON_CARDS.map((key) => (
            <Grid key={key} size={NOTE_CARD_SIZE}>
              <Skeleton variant="rounded" height={SKELETON_HEIGHT} />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (hook.hasError) {
      return (
        <Alert severity="error">
          Les notes n'ont pas pu être chargées. Rechargez la page pour réessayer.
        </Alert>
      );
    }

    if (hook.notes.length === 0) {
      return (
        <EmptyState
          title="Aucune note"
          subtitle="Renseignez ici les informations confidentielles visibles uniquement par les administrateurs et le conseil syndical."
          image={<NoNotesIcon />}
          action={newNoteButton}
        />
      );
    }

    return (
      <Stack spacing={2}>
        <Grid container spacing={2}>
          {hook.notes.map((note) => (
            <Grid key={note.id} size={NOTE_CARD_SIZE}>
              <Card>
                <CardHeader
                  title={note.title}
                  action={hook.isAdmin ? (
                    <ResourceMenuActions
                      size="small"
                      tooltip={`Options de la note ${note.title ?? ''}`.trim()}
                      actions={[
                        { id: 'edit', label: 'Modifier', icon: <EditIcon fontSize="small" />, onClick: () => hook.handleOpenUpdateModal(note) },
                        { id: 'delete', label: 'Supprimer', icon: <DeleteIcon fontSize="small" />, onClick: () => hook.handleOpenDeleteModal(note) },
                      ]}
                    />
                  ) : undefined}
                />
                {note.content ? (
                  <CardContent>
                    <HTMLDisplay content={note.content} />
                  </CardContent>
                ) : null}
              </Card>
            </Grid>
          ))}
        </Grid>
        <InfiniteScrollTrigger
          hasMore={hook.hasMore}
          isLoading={hook.isLoadingMore}
          isError={hook.isLoadMoreError}
          onLoadMore={hook.handleLoadMore}
          label="Notes plus anciennes"
        />
      </Stack>
    );
  }

  return (
    <>
      <FullPageLayout
        htmlTitle="Notes privées"
        title="Notes privées"
        subtitle="Informations confidentielles visibles uniquement par les administrateurs et le conseil syndical."
        action={newNoteButton}
      >
        {renderContent()}
      </FullPageLayout>

      {hook.isCreationModalOpened ? (
        <CreateUpdatePrivateNoteModal onClose={hook.handleCloseCreationModal} />
      ) : null}

      {hook.noteToUpdate ? (
        <CreateUpdatePrivateNoteModal
          onClose={hook.handleCloseUpdateModal}
          note={hook.noteToUpdate}
        />
      ) : null}

      {hook.noteToDelete ? (
        <DeleteNoteModal
          onClose={hook.handleCloseDeleteModal}
          note={hook.noteToDelete}
        />
      ) : null}
    </>
  );
}
